var go = require('gojs');
var diagramConfiguration = require('../../diagramConfiguration');

var defaultGridSize = {
  cols: 7,
  rows: 5
};
var maxSafeCellCount = 10000;

function getCellWidth() {
  return diagramConfiguration.calculateCellWidth();
}

function getCellHeight() {
  return diagramConfiguration.calculateCellHeight();
}

function getResizeCellSize() {
  return new go.Size(getCellWidth(), getCellHeight());
}

function getDefaultGridSize(defaults) {
  return Object.assign({}, defaultGridSize, defaults || {});
}

function normalizeCellCount(value, fallback) {
  var parsed = Number(value);
  if (Number.isFinite(parsed) && parsed > 0) {
    return Math.min(Math.max(Math.round(parsed), 1), maxSafeCellCount);
  }
  return Math.min(Math.max(Math.round(fallback), 1), maxSafeCellCount);
}

function cellCountFromPixelSize(value, cellSize, fallback) {
  var parsed = Number(value);
  if (Number.isFinite(parsed) && parsed > 0 && Number.isFinite(cellSize) && cellSize > 0) {
    return normalizeCellCount(parsed / cellSize, fallback);
  }
  return normalizeCellCount(undefined, fallback);
}

function getGridSize(data, defaults) {
  var fallback = getDefaultGridSize(defaults);
  var cols = normalizeCellCount(
    data && data.cols,
    cellCountFromPixelSize(data && data.width, getCellWidth(), fallback.cols)
  );
  var rows = normalizeCellCount(
    data && data.rows,
    cellCountFromPixelSize(data && data.height, getCellHeight(), fallback.rows)
  );

  return { cols: cols, rows: rows };
}

function getBodySize(data, defaults) {
  var gridSize = getGridSize(data, defaults);
  return new go.Size(
    gridSize.cols * getCellWidth(),
    gridSize.rows * getCellHeight()
  );
}

function normalizeBodySizeForStorage(width, height, defaults) {
  var gridSize = getGridSize({ width: width, height: height }, defaults);
  return {
    cols: gridSize.cols,
    rows: gridSize.rows,
    width: Number((gridSize.cols * getCellWidth()).toFixed(2)),
    height: Number((gridSize.rows * getCellHeight()).toFixed(2))
  };
}

function createBodySizeBinding(defaults) {
  return new go.Binding('desiredSize', '', function(data) {
    return getBodySize(data, defaults);
  });
}

function createExpandedBodySizeBinding(expansion, defaults) {
  return new go.Binding('desiredSize', '', function(data) {
    var bodySize = getBodySize(data, defaults);
    return new go.Size(bodySize.width + expansion, bodySize.height + expansion);
  });
}

function createSelectionAdornmentTemplate() {
  var $ = go.GraphObject.make;
  return $(go.Adornment, 'Auto',
    $(go.Shape, 'Rectangle', {
      fill: null,
      stroke: '#4BA3E3',
      strokeWidth: 0.75
    }),
    $(go.Placeholder)
  );
}

function countPortSlots(data, arrayName) {
  var ports = data && Array.isArray(data[arrayName]) ? data[arrayName] : [];
  return ports.length * 2;
}

function calculatePortPanelOffset(data, arrayName, isHorizontal, defaults) {
  var gridSize = getGridSize(data, defaults);
  var cellSize = isHorizontal ? getCellWidth() : getCellHeight();
  var bodyCellCount = isHorizontal ? gridSize.cols : gridSize.rows;
  var portCellSlots = countPortSlots(data, arrayName);

  if (bodyCellCount <= portCellSlots || portCellSlots === 0) return 0;
  return Math.round((bodyCellCount - portCellSlots) / 2) * cellSize;
}

function createSidePortPanelMarginBinding(arrayName, defaults) {
  return new go.Binding('margin', '', function(data) {
    return new go.Margin(calculatePortPanelOffset(data, arrayName, false, defaults), 0, 0, 0);
  });
}

function createTopBottomPortPanelMarginBinding(arrayName, defaults) {
  return new go.Binding('margin', '', function(data) {
    return new go.Margin(0, 0, 0, calculatePortPanelOffset(data, arrayName, true, defaults));
  });
}

function calculateCenterPortPanelOffset(data, isHorizontal, defaults) {
  var gridSize = getGridSize(data, defaults);
  var cellSize = isHorizontal ? getCellWidth() : getCellHeight();
  var bodyCellCount = isHorizontal ? gridSize.cols : gridSize.rows;
  var portCellSlots = isHorizontal ? countPortSlots(data, 'centerArray') : 2;

  if (bodyCellCount <= portCellSlots || portCellSlots === 0) return 0;
  return Math.round((bodyCellCount - portCellSlots) / 2) * cellSize;
}

function createCenterPortPanelMarginBinding(defaults) {
  return new go.Binding('margin', '', function(data) {
    return new go.Margin(
      calculateCenterPortPanelOffset(data, false, defaults),
      0,
      0,
      calculateCenterPortPanelOffset(data, true, defaults)
    );
  });
}

function normalizeEquipmentPortId(portId) {
  if (portId === undefined || portId === null) return '';
  var parts = portId.toString().split('_');
  return parts.length > 1 ? parts[1] : parts[0];
}

function getPortArray(nodeData, arrayName) {
  return nodeData && Array.isArray(nodeData[arrayName]) ? nodeData[arrayName] : [];
}

function getBoundPortArray(itemPanel, arrayName) {
  if (itemPanel && itemPanel.panel && Array.isArray(itemPanel.panel.itemArray)) {
    return itemPanel.panel.itemArray;
  }
  var nodeData = itemPanel && itemPanel.part && itemPanel.part.data;
  return getPortArray(nodeData, arrayName);
}

function getItemIndex(portData, itemPanel, arrayName) {
  var ports = getBoundPortArray(itemPanel, arrayName);
  var arrayIndex = ports.indexOf(portData);
  if (arrayIndex >= 0) return arrayIndex;
  return Math.max(Number(itemPanel && itemPanel.itemIndex) || 0, 0);
}

function getPortCellIndex(itemIndex, portCount, cellCount) {
  var count = Math.max(Number(portCount) || 0, 1);
  var gridCells = Math.max(Math.round(Number(cellCount) || 0), 1);
  var gap = count > 1 && (1 + ((count - 1) * 2)) <= gridCells ? 2 : 1;
  var occupiedCells = 1 + ((count - 1) * gap);
  var start = Math.max(Math.floor((gridCells - occupiedCells) / 2), 0);
  return Math.max(0, Math.min(gridCells - 1, start + (itemIndex * gap)));
}

function getPortCellCenterOffset(itemIndex, portCount, cellCount, cellSize) {
  return (getPortCellIndex(itemIndex, portCount, cellCount) + 0.5) * cellSize;
}

function getCenterCellOffset(cellCount, cellSize) {
  var gridCells = Math.max(Math.round(Number(cellCount) || 0), 1);
  return (Math.floor((gridCells - 1) / 2) + 0.5) * cellSize;
}

function createSpotPortAlignmentBinding(side, arrayName, defaults) {
  return new go.Binding('alignment', '', function(portData, itemPanel) {
    var nodeData = itemPanel && itemPanel.part && itemPanel.part.data;
    var gridSize = getGridSize(nodeData, defaults);
    var ports = getBoundPortArray(itemPanel, arrayName);
    var itemIndex = getItemIndex(portData, itemPanel, arrayName);
    var cellSize = getResizeCellSize();

    if (side === 'center') {
      var centerXOffset = getPortCellCenterOffset(itemIndex, ports.length, gridSize.cols, cellSize.width);
      return new go.Spot(0, 0, centerXOffset, getCenterCellOffset(gridSize.rows, cellSize.height));
    }

    if (side === 'left' || side === 'right') {
      var yOffset = getPortCellCenterOffset(itemIndex, ports.length, gridSize.rows, cellSize.height);
      return new go.Spot(side === 'left' ? 0 : 1, 0, 0, yOffset);
    }

    var xOffset = getPortCellCenterOffset(itemIndex, ports.length, gridSize.cols, cellSize.width);
    return new go.Spot(0, side === 'top' ? 0 : 1, xOffset, 0);
  });
}

function getSpotPortAlignmentFocus(side) {
  if (side === 'left') return go.Spot.Right;
  if (side === 'right') return go.Spot.Left;
  if (side === 'top') return go.Spot.Bottom;
  if (side === 'bottom') return go.Spot.Top;
  return go.Spot.Center;
}

function getSpotPortLinkSpot(side) {
  if (side === 'left') return go.Spot.Left;
  if (side === 'right') return go.Spot.Right;
  if (side === 'top') return go.Spot.Top;
  if (side === 'bottom') return go.Spot.Bottom;
  return go.Spot.AllSides;
}

function getIsEditing(options) {
  if (options && typeof options.isEditing === 'function') return options.isEditing();
  return true;
}

function createSpotPortItemTemplate(side, arrayName, options) {
  var $ = go.GraphObject.make;
  var settings = options || {};
  var linkSpot = getSpotPortLinkSpot(side);
  var isCenterPort = settings.isCenterPort === true;
  var linkableBindings = isCenterPort ? [
    new go.Binding('visible', '', function() {
      return getIsEditing(settings) ? true : false;
    }),
    new go.Binding('fromLinkable', '', function() {
      return true;
    }),
    new go.Binding('toLinkable', '', function() {
      return true;
    })
  ] : [
    new go.Binding('fromLinkable', 'type', function(type) {
      return type === 'output';
    }),
    new go.Binding('toLinkable', 'type', function(type) {
      return type === 'input';
    })
  ];
  if (isCenterPort && settings.allowMultipleLinks !== true) {
    linkableBindings.push(
      new go.Binding('fromMaxLinks', '', function() {
        return 1;
      }),
      new go.Binding('toMaxLinks', '', function() {
        return 1;
      })
    );
  }

  return $(go.Panel, 'Spot',
    {
      alignmentFocus: getSpotPortAlignmentFocus(side)
    },
    createSpotPortAlignmentBinding(side, arrayName, settings.defaults),
    $(go.Shape, 'Rectangle',
      {
        _side: side,
        cursor: 'pointer',
        desiredSize: getResizeCellSize(),
        fromSpot: linkSpot,
        toSpot: linkSpot,
        stroke: null,
        strokeWidth: 0
      },
      ...linkableBindings,
      new go.Binding('portId', 'portId', normalizeEquipmentPortId),
      new go.Binding('category', 'category'),
      new go.Binding('fill', 'portColor', function(portColor) {
        if (!getIsEditing(settings) && settings.portCoverBrush) return settings.portCoverBrush;
        return portColor;
      })
    )
  );
}

function createSpotPortPanel(arrayName, side, options) {
  var $ = go.GraphObject.make;
  var settings = options || {};
  return $(go.Panel, 'Spot',
    {
      alignment: go.Spot.Center,
      alignmentFocus: go.Spot.Center,
      isActionable: false,
      itemTemplate: createSpotPortItemTemplate(side, arrayName, settings)
    },
    new go.Binding('itemArray', arrayName),
    $(go.Shape, 'Rectangle',
      {
        isPanelMain: true,
        fill: null,
        stroke: null
      },
      createBodySizeBinding(settings.defaults)
    )
  );
}

module.exports = {
  createBodySizeBinding: createBodySizeBinding,
  createCenterPortPanelMarginBinding: createCenterPortPanelMarginBinding,
  createExpandedBodySizeBinding: createExpandedBodySizeBinding,
  createSelectionAdornmentTemplate: createSelectionAdornmentTemplate,
  createSidePortPanelMarginBinding: createSidePortPanelMarginBinding,
  createSpotPortPanel: createSpotPortPanel,
  createTopBottomPortPanelMarginBinding: createTopBottomPortPanelMarginBinding,
  getBodySize: getBodySize,
  getGridSize: getGridSize,
  getResizeCellSize: getResizeCellSize,
  normalizeBodySizeForStorage: normalizeBodySizeForStorage
};
